import {AfterViewInit, Component, OnDestroy, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {ReplaySubject} from 'rxjs';
import {UserPermissions} from '../../../../shared/interface/user-permissions';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatDialog} from '@angular/material/dialog';
import {AuthService} from '../../../../shared/services/auth.service';
import {ActivatedRoute, Router} from '@angular/router';
import {FilterService} from '../../../../shared/services/filter.service';
import {select, Store} from '@ngrx/store';
import {StoreRootState} from '../../../../shared/ngrx/reducers';
import {delay, distinctUntilChanged, takeUntil, tap} from 'rxjs/operators';
import {CountedAndPaginated} from '../../../../shared/interface/response/counted-and-paginated';
import {getCurrentRouteState} from '../../../../shared/ngrx/selector';
import {RouterStateUrl} from '../../../../shared/ngrx/route-serializer';
import {Uuid} from '../../../../shared/interface/response/uuid';
import {JobResultDialogComponent} from '../../../../shared/components/job-result-dialog/job-result-dialog.component';
import {HttpErrorResponse} from '@angular/common/http';
import {ConfirmDialogComponent} from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import {CommissionRedirection} from '../../../../shared/interface/commission-redirection';
import {CommissionRedirectionService} from '../../../../shared/services/commission-redirection.service';
import {CrossCompanyReportDialogComponent} from '../cross-company-report-dialog/cross-company-report-dialog.component';
import {CommissionImportRowService} from '../../../../shared/services/commission-import-row.service';

@Component({
  selector: 'app-all-redirections-list',
  templateUrl: './all-redirections-list.component.html',
  styleUrls: ['./all-redirections-list.component.scss']
})
export class AllRedirectionsListComponent implements OnDestroy, AfterViewInit {
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  initialFilters: any;
  items: CommissionRedirection[] = [];
  totalData = 0;
  displayedColumns: string[] = ['insuranceCompany', 'company', 'ofwca', 'policyNumber',
    'ratePaid', 'newOfwca', 'newPartner', 'OfwcaAssetsRegional', 'newPartnerAssetsRegional', 'actions', ];
  userPermissions: ReplaySubject<UserPermissions>;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private commissionRedirectionService: CommissionRedirectionService,
    private snack: MatSnackBar,
    private dialog: MatDialog,
    private auth: AuthService,
    private activatedRoute: ActivatedRoute,
    private filterService: FilterService,
    private store: Store<StoreRootState>,
    private router: Router,
    private commissionImportRowService: CommissionImportRowService,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.commissionRedirectionService.commissionRedirectionListSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<CommissionRedirection>) => {
          this.items = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      in$insuranceCompany: 'int[]',
      contains$policyNumber: 'string',
      company: 'int',
      ofwca: 'string',
      contains$customerName: 'string',
      gte$concludeDate: 'date',
      lte$concludeDate: 'date',
      newOfwca: 'string',
      newPartner: 'string',
      newPartnerAssetsRegional: 'string',
      gte$ratePaid: 'string',
      lte$ratePaid: 'string',
      gte$assign: 'string',
      lte$assign: 'string',
    }, filters);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: event.pageIndex,
        pageSize: event.pageSize,
        ...this.filtersForm.value
      })]);
    });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((state: RouterStateUrl) => {
        this.parseFilters(state.params);
        this.paginator.pageIndex = state.params.page;
        this.paginator.pageSize = state.params.pageSize;
        this.loadListData();
      })
    ;
  }

  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value,
    })]);
  }

  loadListData(): void {
    this.commissionRedirectionService.loadList(this.paginator.pageIndex, this.paginator.pageSize, this.initialFilters);
  }

  downloadReport(): void {
    this.commissionRedirectionService.downloadReport(this.initialFilters)
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  downloadCrossCompanyReport(): void {
    const dialogRef = this.dialog.open(CrossCompanyReportDialogComponent, {
      width: '350px',
    });

    dialogRef.afterClosed().subscribe((result?: string) => {
      if (!result) {
        return;
      }

      this.commissionImportRowService.downloadReport({crossCompanyMonth: result})
        .subscribe((response: Uuid) => {
          this.dialog.open(JobResultDialogComponent, {
            disableClose: true,
            width: '500px',
            data: {jobId: response.uuid, showProgress: false},
          });
        }, (error: HttpErrorResponse) => {
          this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
        });
    });
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }

  delete(commissionRedirection: CommissionRedirection): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć przepięcie?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.commissionRedirectionService.delete(commissionRedirection.uuid)
          .subscribe({
            next: () => {
              this.snack.open('Pomyślnie usunięto przepięcie', '', {duration: 2000});
              this.loadListData();
            },
            error: (response: HttpErrorResponse) => {
              this.snack.open(response.error.error.text, response.status.toString(), {duration: 2000});
            }
          });
      }
    });
  }
}
